import { Fragment } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { cachedStringSearch } from '@recruitee/emoji';

import { OverlayPosition, RtSchemaType } from '../../editor/types';
import { MentionNode } from './nodes';
import {
  MentionElement,
  MentionListController,
  MentionsListOpenOptions,
  MentionsPluginEvents,
  MentionsPluginPickerClose,
  MentionsPluginPickerBeforeOpen,
} from './types';

export class MentionServiceInternal {
  public data: MentionElement[];
  public filterData = cachedStringSearch(query =>
    this.data.filter(item => item.name.toLowerCase().includes(query)),
  );

  public currentOrigin: number | HTMLElement;
  private currentQuery: string;
  private preferredPickerPositions = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' } as const,
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' } as const,
  ];

  constructor(
    private editorView: EditorView<RtSchemaType<typeof MentionNode>>,
    private mentionListController: MentionListController,
    private dataSource: Observable<MentionElement[]>,
    private eventStream$: Subject<MentionsPluginEvents>,
    private scroll$: Observable<void>,
    private destroy$: Observable<void>,
  ) {
    this.dataSource.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.data = data;
      if (!this.mentionListController.isOpen()) return;

      const filteredData = this.filterData(this.currentQuery);
      this.mentionListController.setData(filteredData);
    });

    this.scroll$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.mentionListController.isOpen()) return;

      const position = this.getPositionFromCurrentOrigin();
      this.mentionListController.updatePosition(position);
    });
  }

  public mentionsMatchHandler(
    state: EditorState,
    match: RegExpMatchArray | null,
    start: number,
    end: number,
  ) {
    const mentionsOpen = this.mentionListController.isOpen();
    if (!match) return this.closeMentionsList();
    const matchedText = match[0];

    const query = (this.currentQuery = matchedText.replace('@', ''));
    const filteredData = this.filterData(query);
    if (!filteredData.length) return this.closeMentionsList();

    const from = matchedText[0].match(/\s/) ? start + 1 : start;
    this.currentOrigin = from;

    if (!mentionsOpen) {
      const position = this.editorView.coordsAtPos(from);
      this.openMentionsList(position, {
        preferredPositions: this.preferredPickerPositions,
      });
    }

    this.updateMentionListData(from, end, filteredData);
  }

  public updateMentionListData(from: number, to: number, data: MentionElement[]) {
    this.mentionListController.setSelectCallback(this.getSelectMentionCallback(from, to));
    this.mentionListController.setData(data);
  }

  public openMentionsList(position: OverlayPosition, options?: MentionsListOpenOptions) {
    this.eventStream$.next(new MentionsPluginPickerBeforeOpen());
    const { backdropClick$ } = this.mentionListController.open(position, options);
    backdropClick$.subscribe(() => {
      this.closeMentionsList();
      this.editorView.focus();
    });
  }

  public closeMentionsList(): void {
    this.mentionListController.close();
    this.eventStream$.next(new MentionsPluginPickerClose());
  }

  private getPositionFromCurrentOrigin() {
    if (this.currentOrigin instanceof HTMLElement) {
      return this.currentOrigin.getBoundingClientRect();
    } else {
      return this.editorView.coordsAtPos(this.currentOrigin);
    }
  }

  private getSelectMentionCallback(start: number, end: number) {
    return (selectedEl: MentionElement) => {
      const { state } = this.editorView;
      const textNode = state.schema.text(selectedEl.name);
      const mentionNode = state.schema.nodes.mention.createChecked(
        {
          'data-id': selectedEl.id,
          'data-type': selectedEl.type,
        },
        textNode,
      );

      const { tr } = state;
      const fragment = Fragment.from([mentionNode, state.schema.text(' ')]);
      tr.replaceWith(start, end, fragment);
      this.editorView.dispatch(tr);

      this.closeMentionsList();
      this.editorView.focus();
    };
  }
}
